import slugify from 'slugify'
import { normalizeId } from '@/lib/utils/string'

/**
 * Generates a SEO-friendly URL for a product
 *
 * @param item - The product item or line item with payload
 * @param fallbackId - Optional fallback ID to use in slugify if no other identifier is found
 * @returns The SEO-friendly URL slug
 */
export const generateSeoUrl = (item: any, fallbackId?: string): string => {
  if (!item) return ''

  // Check if this is a cart line item with payload or a product item
  const isLineItem = !!item.payload || item.type === 'product'

  // PRIORITY 1: Direct access from extensions.seoUrls with seoPathInfo property
  // Works for both product items and cart line items in new Shopware structure
  if (item.extensions?.seoUrls?.seoPathInfo) {
    return item.extensions.seoUrls.seoPathInfo
  }

  // PRIORITY 2: Old array format at root level
  if (item.seoUrls && Array.isArray(item.seoUrls) && item.seoUrls.length > 0) {
    const seoUrl = item.seoUrls.find(
      (url: any) =>
        url &&
        typeof url === 'object' &&
        'seoPathInfo' in url &&
        url.seoPathInfo,
    )
    if (seoUrl && seoUrl.seoPathInfo) {
      return seoUrl.seoPathInfo
    }
  }

  // PRIORITY 3: For cart line items, check if references has seoUrls
  if (isLineItem && item.references) {
    if (
      item.references.seoUrls &&
      Array.isArray(item.references.seoUrls) &&
      item.references.seoUrls.length > 0
    ) {
      const seoUrl = item.references.seoUrls.find(
        (url: any) =>
          url &&
          typeof url === 'object' &&
          'seoPathInfo' in url &&
          url.seoPathInfo,
      )
      if (seoUrl && seoUrl.seoPathInfo) {
        return seoUrl.seoPathInfo
      }
    }
  }

  // Handle different objects structures (product item vs line item)
  const product = item
  const payload = isLineItem ? item.payload : item

  // Start with the product slug if available (for product items)
  let slug = product.slug || ''

  // PRIORITY 4: For cart line items with referencedId and label
  if (isLineItem && item.referencedId && item.label) {
    const sapId = normalizeId(payload?.customFields?.sap_id)

    // Ensure we have valid strings before slugifying
    const label = typeof item.label === 'string' ? item.label : ''
    if (!label) return item.referencedId || ''

    if (sapId) {
      return slugify(`${label}-${sapId}`, {
        lower: true,
        strict: true,
        trim: true,
      })
    }

    return slugify(label, { lower: true, strict: true, trim: true })
  }

  // PRIORITY 5: Check other seoUrl locations for product items
  if (!isLineItem || !slug) {
    // 1. Check seoUrls at payload level
    if (
      payload?.seoUrls &&
      Array.isArray(payload.seoUrls) &&
      payload.seoUrls.length > 0
    ) {
      const seoUrl = payload.seoUrls.find(
        (url: any) =>
          url &&
          typeof url === 'object' &&
          'seoPathInfo' in url &&
          url.seoPathInfo,
      )
      if (seoUrl && seoUrl.seoPathInfo) {
        slug = seoUrl.seoPathInfo
      }
    }

    // 2. Check in children if parent doesn't have seoUrls
    if (
      !slug &&
      payload?.children &&
      Array.isArray(payload.children) &&
      payload.children.length > 0
    ) {
      for (const child of payload.children) {
        if (
          child.seoUrls &&
          Array.isArray(child.seoUrls) &&
          child.seoUrls.length > 0
        ) {
          const childSeoUrl = child.seoUrls.find(
            (url: any) =>
              url &&
              typeof url === 'object' &&
              'seoPathInfo' in url &&
              url.seoPathInfo,
          )
          if (childSeoUrl && childSeoUrl.seoPathInfo) {
            slug = childSeoUrl.seoPathInfo
            break // Stop once we find a valid seoUrl
          }
        }
      }
    }

    // 3. Check in extensions.search if needed
    if (
      !slug &&
      payload?.extensions?.search?.seoUrls &&
      Array.isArray(payload.extensions.search.seoUrls)
    ) {
      const seoUrl = payload.extensions.search.seoUrls.find(
        (url: any) =>
          url &&
          typeof url === 'object' &&
          'seoPathInfo' in url &&
          url.seoPathInfo,
      )
      if (seoUrl && seoUrl.seoPathInfo) {
        slug = seoUrl.seoPathInfo
      }
    }
  }

  // Fallback to slugify if seoPathInfo is not available
  if (!slug) {
    // Get appropriate name and ID fields based on object structure
    const name = product.translated?.name || product.name || product.label || ''

    slug = slugify(`${name}-undefined`, {
      lower: true,
      strict: true,
      trim: true,
    })
  }

  return slug
}
